import { Module } from 'vuex';
import { Preset, PresetApi } from '@/utils/api';
import { AxiosResponse } from 'axios';
import { RootState } from '..';
import loadImgSrc from '@/utils/helpers/loadImgSrc';
import getOutsideLayers from '@/utils/helpers/getOutsideLayers';
import { presetMediaDimensions } from '@/components/PresetMedia/PresetMedia.models';
import { ResponsiveImageMedia } from '@/components/utils/ResponsiveImage/ResponsiveImage.models';
import { Layer } from '@/components/LayeringEngine/LayeringEngine.models';

const SET_PRESETS = 'SET_PRESETS';
const RESET_STATE = 'RESET_STATE';
const SET_CURRENT_INDEX = 'SET_CURRENT_INDEX';

export interface HomeState {
  presets: Preset[];
  currentPresetIndex: number;
}

const homeInitialState = (): HomeState => ({
  presets: [],
  currentPresetIndex: 0
});

const presetApi = new PresetApi();

export const homeModule: Module<HomeState, RootState> = {
  namespaced: true,
  state: homeInitialState(),
  getters: {
    numberOfPresets(state): number {
      return state.presets.length;
    },
    isLastPreset(state, getters): boolean {
      return state.currentPresetIndex >= getters.numberOfPresets - 1;
    },
    isFirstPreset(state): boolean {
      return state.currentPresetIndex <= 0;
    }
  },
  actions: {
    async setPresets({ commit, rootState, rootGetters }) {
      if (rootGetters['configuration/selectedType']) {
        const typesResponse: AxiosResponse<Preset[]> = await presetApi.getPresets(
          rootGetters['configuration/selectedType'].ID,
          rootState.app.locale
        );
        const types: Preset[] = typesResponse.data;

        commit(SET_PRESETS, types);

        return types;
      }
    },
    async preloadPresetImages({ state, rootState }) {
      const firstPreset: Preset = state.presets[0];

      if (firstPreset) {
        await Promise.allSettled(
          getOutsideLayers(firstPreset.configuration, presetMediaDimensions).map((layer: Layer) =>
            loadImgSrc(layer.media[rootState.app.imageMQ as keyof ResponsiveImageMedia])
          )
        );
      }

      return;
    },
    resetState({ commit }) {
      commit(RESET_STATE);
    },
    increaseCurrentIndex({ commit, getters, state }) {
      if (!getters.isLastPreset) {
        commit(SET_CURRENT_INDEX, state.currentPresetIndex + 1);
      }
    },
    decreaseCurrentIndex({ commit, getters, state }) {
      if (!getters.isFirstPreset) {
        commit(SET_CURRENT_INDEX, state.currentPresetIndex - 1);
      }
    },
    setCurrentIndex({ commit, getters }, newIndex: number) {
      if (0 <= newIndex && newIndex < getters.numberOfPresets) {
        commit(SET_CURRENT_INDEX, newIndex);
      }
    }
  },
  mutations: {
    [SET_CURRENT_INDEX](state, currentIndex: number) {
      state.currentPresetIndex = currentIndex;
    },
    [SET_PRESETS](state, presets: Preset[]) {
      state.presets = presets;
    },
    [RESET_STATE](state) {
      Object.assign(state, homeInitialState());
    }
  }
};
